import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  CONSUMER_CMS_SHOPPING_CART,
  errorMock,
  getFwToken,
  IGlobalConfigCMS,
  IShoppingCartResponse,
  SELECTED_PAYMENT,
  useAuthorizationOrder,
  useCmsConfig,
  useGetPaymean,
  useLightCreditVettingMutation,
  useNotify,
  usePaymentDistributionMobile,
  useReserveEquipmentMutation,
  useReserveUnreserveVoucher,
  useSubmitMobileMutation,
  useSubmitWithPaymentMobile,
  useValidateIbansMobile,
} from '@vfit/consumer/data-access';
import { ITrackError, LoggerInstance } from '@vfit/shared/data-access';
import { EMethodCode, Path } from '@vfit/shared/models';
import {
  API,
  getFromLocalStorageByKey,
  usePostCustomerEligibility,
} from '@vfit/shared/data-access';
import { useCheckout } from '../../../iBuyMobile.context';
import { useTpoCheck } from '../../../ShoppingCart/hooks/useTpoCheck';

interface IUseCheckoutMobileFlow {
  ignoreQueryString?: boolean;
}

// API FLOW
// Check eligibility
// Authorization
// Paymean
// Payment distribution
// validate iban only if is IBAN
// reserve equipment
// light credit vetting
// modifyPhoneNumberPortability (only if the portIn was choosen in checkout flow)
// reserve voucher only if voucher is submitted
// postCustomer
// submit / submitWithPayment
// post eligibility if is TPO e NOT CAMPAIGN

export const useCheckoutFlow = ({ ignoreQueryString }: IUseCheckoutMobileFlow) => {
  const shoppingCart: IShoppingCartResponse = getFromLocalStorageByKey('shoppingCart');
  const queryString = window.location.search;
  const { portability, portabilityStatus, voucher, setPortabilityStatus } = useCheckout();
  const globalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;

  // setting internal hook state
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<ITrackError>({
    errorMessage: '',
    error: undefined,
  });

  const queryClient = useQueryClient();
  const authorizationError: ITrackError | undefined =
    queryClient.getQueryData('authorizationError');
  const modifyPhoneNumberProductError: ITrackError | undefined = queryClient.getQueryData(
    'modifyPhoneNumberProductQuery'
  );
  const isAppliedVoucher = voucher?.isAppliedVoucher || false;

  // (1) Check eligibility
  const {
    isTpoOffer,
    isInCampaignTpo,
    isLoadingCheck,
    isEligible,
    token,
    isEnabledFwAmdIntegration,
  } = useTpoCheck();

  // (2) calling hook mutation
  const {
    refetch: refetchAuthorization,
    data: authorizationOrderData,
    isSuccess: isSuccessAuthorizationOrder,
    isError: isErrorAuthorizationOrder,
    isLoading: isLoadingAuthorizationOrder,
  } = useAuthorizationOrder({
    enabled: false,
  });
  const {
    error: validateIbanError,
    isLoading: isLoadingValidateIban,
    mutate: mutateValidateIban,
    isError: isErrorValidateIban,
    isSuccess: isSuccessValidateIban,
  } = useValidateIbansMobile();

  const { isError: isErrorPayMean, isLoading: isLoadingPayMean } = useGetPaymean(
    undefined,
    globalConfig
  );

  // (3) calling notify
  const notifyMutation = useNotify();

  // (4) calling hook mutation
  const paymentDistributionMutation = usePaymentDistributionMobile();
  const selectedPaymentMethod = localStorage.getItem(SELECTED_PAYMENT);

  // (5) calling hook mutation
  const {
    data: reserveEquipmentData,
    error: reserveEquipmentError,
    isLoading: isLoadingReserve,
    isSuccess: isSuccessReserve,
    isError: isErrorReserve,
    mutate: mutateReserve,
  } = useReserveEquipmentMutation();

  // (6) calling hook mutation or lightCreditVettingMutation
  const lightCreditVettingMutation = useLightCreditVettingMutation();

  // (7) calling hook mutation final
  const submitCCMutation = useSubmitMobileMutation();
  const submitWithPaymentMutation = useSubmitWithPaymentMobile();
  const {
    isLoading: isLoadingSubmit,
    isError: isErrorSubmit,
    isSuccess: isSuccessSubmit,
    data: submitData,
    error: submitError,
    mutate: mutateSubmit,
  } = selectedPaymentMethod?.toLowerCase() === EMethodCode.CREDIT_CARD.toLowerCase()
    ? submitCCMutation
    : submitWithPaymentMutation;

  // (8) post submit eligibility
  const {
    isLoading: isLoadingPostCustomerEligibility,
    isSuccess: isSuccessPostCustomerEligibility,
    isError: isErrorPostCustomerEligibility,
    mutate: mutatePostCustomerEligibility,
  } = usePostCustomerEligibility();

  // (9) Reserve/un-reserve voucher
  const {
    reset: resetReserveUnreserveVoucher,
    mutateAsync: reserveUnreserveVoucherMutate,
    isLoading: isLoadingReserveUnreserveVoucher,
    isSuccess: isSuccessReserveUnreserveVoucher,
    isError: isErrorReserveUnreserveVoucher,
    error: errorReserveUnreserveVoucher,
  } = useReserveUnreserveVoucher();

  const afterSubmitForFastwebAndAmdocsIntegration = () => {
    const fwToken = getFwToken();
    if (!fwToken && isTpoOffer && isInCampaignTpo) {
      setIsError(false);
      setIsLoading(false);
      setIsSuccess(true);
    } else if (fwToken && submitData?.fwStatus === 'KO') {
      setIsLoading(false);
      setIsSuccess(false);
      setIsError(true);
      setError({
        errorCode: submitData?.fwStatusCode,
      });
    } else if (fwToken) {
      mutatePostCustomerEligibility({
        t: getFwToken() || '',
        idOrder: shoppingCart?.id,
      });
    } else {
      setIsError(false);
      setIsLoading(false);
      setIsSuccess(true);
    }
  };

  const afterSubmitStandard = () => {
    if (isTpoOffer && !isInCampaignTpo) {
      mutatePostCustomerEligibility({
        t: token || '',
        idOrder: shoppingCart?.id,
      });
    } else {
      setIsError(false);
      setIsLoading(false);
      setIsSuccess(true);
    }
  };

  const sumitOrderError = () => {
    const voucherBoId = voucher?.validateVoucher?.data?.parentBoId;
    if (!isAppliedVoucher || !voucherBoId) {
      setIsLoading(false);
      setIsSuccess(false);
      setIsError(true);
      setError(
        errorMock(
          `submit`,
          submitError
            ? submitError
            : {
                status: '500',
                statusText: 'KO',
                url:
                  selectedPaymentMethod?.toLowerCase() === EMethodCode.CREDIT_CARD.toLowerCase()
                    ? API.SUBMIT
                    : API.SUBMIT_WITH_PAYMENT,
              }
        )
      );
    } else {
      reserveUnreserveVoucherMutate({
        boArray: [voucherBoId],
        voucherId: voucher?.voucherId || '',
        status: 'unreserve',
      });
    }
  };

  const submitOrder = () => {
    const voucherBoId = voucher?.validateVoucher?.data?.parentBoId;
    if (!isAppliedVoucher || !voucherBoId) mutateSubmit();
    else {
      reserveUnreserveVoucherMutate({
        boArray: [voucherBoId],
        voucherId: voucher?.voucherId || '',
        status: 'reserve',
      });
    }
  };

  useEffect(() => {
    if (isAppliedVoucher) {
      if (!isErrorSubmit) {
        if (isErrorReserveUnreserveVoucher) {
          setIsLoading(false);
          setIsSuccess(false);
          setIsError(true);
          setError(errorReserveUnreserveVoucher as ITrackError);
        } else if (isSuccessReserveUnreserveVoucher) {
          mutateSubmit();
          resetReserveUnreserveVoucher();
        }
      } else {
        if (isErrorReserveUnreserveVoucher || isSuccessReserveUnreserveVoucher) {
          setIsLoading(false);
          setIsSuccess(false);
          setIsError(true);
          setError(
            errorMock(
              `submit`,
              submitError
                ? submitError
                : {
                    status: '500',
                    statusText: 'KO',
                    url:
                      selectedPaymentMethod?.toLowerCase() === EMethodCode.CREDIT_CARD.toLowerCase()
                        ? API.SUBMIT
                        : API.SUBMIT_WITH_PAYMENT,
                  }
            )
          );
        }
      }
    }
  }, [
    isSuccessReserveUnreserveVoucher,
    isLoadingReserveUnreserveVoucher,
    isErrorReserveUnreserveVoucher,
  ]);

  /**
   * Submit for portability condition
   */
  useEffect(() => {
    if (
      portabilityStatus?.isSuccess &&
      portabilityStatus?.isStartFinalPortIn &&
      lightCreditVettingMutation?.isSuccess &&
      !isSuccessSubmit &&
      !isLoadingSubmit &&
      !isErrorSubmit
    ) {
      submitOrder();
    } else if (portabilityStatus?.isError) {
      setIsLoading(false);
      setIsSuccess(false);
      setIsError(true);
      setError(modifyPhoneNumberProductError as ITrackError);
    }
  }, [portabilityStatus, lightCreditVettingMutation?.isSuccess, isSuccessSubmit, isLoadingSubmit]);

  useEffect(() => {
    if (!isLoadingPostCustomerEligibility) {
      if (isSuccessPostCustomerEligibility || isErrorPostCustomerEligibility) {
        setIsError(false);
        setIsLoading(false);
        setIsSuccess(true);
      }
    }
  }, [
    isSuccessPostCustomerEligibility,
    isLoadingPostCustomerEligibility,
    isErrorPostCustomerEligibility,
  ]);

  /**
   * After Submit hook to finalize the flow
   * setting internal state isLoading=false && isSuccess=true
   */
  useEffect(() => {
    if (!isLoadingSubmit) {
      if (isSuccessSubmit) {
        if (submitData?.status?.toLowerCase() === 'softko') {
          setIsLoading(false);
          setIsSuccess(false);
          setIsError(true);
          setError(
            errorMock(`submit`, {
              status: '200',
              statusText: 'SoftKO',
              url:
                selectedPaymentMethod?.toLowerCase() === EMethodCode.CREDIT_CARD.toLowerCase()
                  ? API.SUBMIT
                  : API.SUBMIT_WITH_PAYMENT,
            })
          );
        } else {
          if (isEnabledFwAmdIntegration) {
            afterSubmitForFastwebAndAmdocsIntegration();
          } else {
            afterSubmitStandard();
          }
        }
      } else if (isErrorSubmit) {
        sumitOrderError();
      }
    }
  }, [isSuccessSubmit, isLoadingSubmit, isErrorSubmit, submitError]);

  useEffect(() => {
    if (isErrorValidateIban) {
      setIsLoading(false);
      setIsSuccess(false);
      setIsError(true);
      setError(validateIbanError as ITrackError);
    } else if (isSuccessValidateIban) {
      mutateReserve();
    }
  }, [isSuccessValidateIban, isLoadingValidateIban, isErrorValidateIban]);

  /**
   * LightCreditVetting conditioned by portability if chosen
   */
  useEffect(() => {
    if (!lightCreditVettingMutation.isLoading) {
      if (lightCreditVettingMutation.isSuccess) {
        LoggerInstance.debug(
          'useCheckoutFlow lightCreditVettingMutation.data',
          lightCreditVettingMutation.data
        );
        if (portability?.portabilityWant && portabilityStatus?.isSuccessCheck) {
          setPortabilityStatus({
            ...portabilityStatus,
            isStartFinalPortIn: true,
          });
        } else if (!portability?.portabilityWant && !isErrorSubmit) {
          submitOrder();
        }
      } else if (lightCreditVettingMutation.isError) {
        setIsLoading(false);
        setIsSuccess(false);
        setIsError(true);
        setError(
          errorMock(
            `lightCreditVetting`,
            lightCreditVettingMutation?.error
              ? lightCreditVettingMutation?.error
              : {
                  status: '500',
                  statusText: 'KO',
                  url: API.LIGHT_CREDIT_VETTING,
                }
          )
        );
      }
    }
  }, [
    lightCreditVettingMutation.isLoading,
    lightCreditVettingMutation.isSuccess,
    lightCreditVettingMutation.isError,
    portability,
  ]);

  /**
   * handlerPaymentType hook conditioned by reserveEquipmentMutation.data
   */
  useEffect(() => {
    if (!isLoadingReserve) {
      if (isSuccessReserve) {
        if (reserveEquipmentData?.status === 'KO') {
          setIsLoading(false);
          setIsSuccess(false);
          setIsError(true);
          setError(
            errorMock(
              'reserveEquipment',
              {
                status: '200',
                statusText: 'KO',
                url: API.RESERVE_EQUIPMENT,
              },
              'KO'
            )
          );
        } else {
          lightCreditVettingMutation.mutate();
        }
      } else if (isErrorReserve) {
        setIsLoading(false);
        setIsSuccess(false);
        setIsError(true);
        setError(
          errorMock(
            `reserveEquipment`,
            reserveEquipmentError
              ? reserveEquipmentError
              : {
                  status: '500',
                  statusText: 'KO',
                  url: API.RESERVE_EQUIPMENT,
                }
          )
        );
      }
    }
  }, [isSuccessReserve, isErrorReserve, isLoadingReserve, reserveEquipmentError]);

  /**
   * ReserveEquipment mutation initializer conditioned by paymentDistributionMutation.data
   */
  useEffect(() => {
    if (!paymentDistributionMutation.isLoading) {
      if (paymentDistributionMutation.isSuccess) {
        if (paymentDistributionMutation.data?.error) {
          setIsLoading(false);
          setIsSuccess(false);
          setIsError(true);
          setError(paymentDistributionMutation.data as ITrackError);
        } else if (
          authorizationOrderData?.authorization?.payMean?.type?.toLowerCase() ===
          EMethodCode.BANK_ACCOUNT
        ) {
          mutateValidateIban();
        } else {
          mutateReserve();
        }
      } else if (paymentDistributionMutation.isError) {
        setIsLoading(false);
        setIsSuccess(false);
        setIsError(true);
        setError(
          errorMock(
            `paymentDistribution`,
            paymentDistributionMutation?.error
              ? paymentDistributionMutation?.error
              : {
                  status: '500',
                  statusText: 'KO',
                  url: API.PAYMENT_DISTRIBUTION,
                }
          )
        );
      }
    }
  }, [
    paymentDistributionMutation.isLoading,
    paymentDistributionMutation.isSuccess,
    paymentDistributionMutation.isError,
  ]);

  /**
   * PaymentDistribution mutation initializer conditioned by authorizationOrderData
   */
  useEffect(() => {
    LoggerInstance.debug('queryString', queryString);
    if (queryString?.includes(Path.URL_FORWARD) || ignoreQueryString) {
      if (isSuccessAuthorizationOrder && !isLoadingAuthorizationOrder) {
        if (authorizationError?.error) {
          setIsLoading(false);
          setIsSuccess(false);
          setIsError(true);
          setError(authorizationError);
        } else {
          LoggerInstance.debug('useCheckoutFlow authorizationOrderData : ', authorizationOrderData);
          LoggerInstance.debug('useCheckoutFlow calling paymentDistributionMutation.mutate()');
          notifyMutation.mutate();
          paymentDistributionMutation.mutate();
        }
      } else if (
        (isErrorPayMean && !isLoadingPayMean) ||
        (isErrorAuthorizationOrder && !isLoadingAuthorizationOrder)
      ) {
        setIsLoading(false);
        setIsSuccess(false);
        setIsError(true);
        const paymeanError: ITrackError = getFromLocalStorageByKey('payMean');
        setError(paymeanError);
      }
    }
  }, [
    isLoadingAuthorizationOrder,
    isErrorAuthorizationOrder,
    isSuccessAuthorizationOrder,
    isErrorPayMean,
  ]);

  useEffect(() => {
    if (!isLoadingCheck) {
      if (!isTpoOffer || (isTpoOffer && isEligible) || (isTpoOffer && !isEnabledFwAmdIntegration)) {
        refetchAuthorization();
      }
    }
  }, [isLoadingCheck]);

  return {
    isLoading,
    isSuccess,
    isError,
    error,
  };
};
